import React, { useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { savePld, createPldMoral, updatePldMoral } from 'api/client';
import OnboardingSaveSteps from 'context/onboarding-save-steps/context';
import OnboardingMoralPersonSaveSteps from 'context/onboarding-moral-person-save-steps/context';
import PLDSaveSteps from './context';

const initialState = { saved: false };

interface Props {
  children: React.ReactNode;
}

const PLDSaveStepsProvider = ({ children }: Props) => {
  const { pathname } = useLocation();
  const isMoralP = String(pathname).includes(`/clientes/moral/`);
  const { dataStepGeneral } = useContext(OnboardingSaveSteps);
  const { dataStepPersonal } = useContext(OnboardingMoralPersonSaveSteps);

  const [dataStepPLD, setDataStepPLD] = useState<any>(initialState);

  const pldMoralPersonDataRemapper = (values: any) => {
    const addArrFormat = (value: any[]) => {
      if (value) return value.map((i) => i.value);
      return [];
    };

    let cveForeignTransferCountry =
      values.cveForeignTransferCountry?.value || null;
    if (!values.receivesForeignTransfers && !values.sendsForeignTransfers) {
      cveForeignTransferCountry = null;
    }

    return {
      contractId: dataStepPersonal.contractId,
      clientId: dataStepPersonal.clientId,
      depositCurrency:
        (values.depositAmountCurrency && values.depositAmountCurrency.value) ||
        0,
      depositType:
        (values.depositAmountType && values.depositAmountType.value) || 0,
      depositFrequency:
        (values.depositFrequencyType && values.depositFrequencyType.value) || 0,
      depositNumber: values.depositFrequency || 0,
      resourceDestination: addArrFormat(values.resourceDestination),
      resourceOrigin: addArrFormat(values.resourceOrigin),
      withdrawalCurrency:
        (values.withdrawalAmountCurrency &&
          values.withdrawalAmountCurrency.value) ||
        0,
      withdrawalFrequency:
        (values.withdrawalFrequencyType &&
          values.withdrawalFrequencyType.value) ||
        0,
      withdrawalNumber: values.withdrawalFrequency || 0,
      withdrawalType:
        (values.withdrawalAmountType && values.withdrawalAmountType.value) || 0,
      withdrawalAmount: parseFloat(values.withdrawalAmount.value),
      depositAmount: parseFloat(values.depositAmount.value),
      receivesForeignTransfers: values.receivesForeignTransfers
        ? `${values.receivesForeignTransfers}`
        : null,
      sendsForeignTransfers: values.sendsForeignTransfers
        ? `${values.sendsForeignTransfers}`
        : null,
      cveForeignTransferCountry: cveForeignTransferCountry,
    };
  };

  const PLDDataRemapper = (values: any) => {
    let cveForeignTransferCountry =
      values.cveForeignTransferCountry?.value || null;
    if (!values.receivesForeignTransfers && !values.sendsForeignTransfers) {
      cveForeignTransferCountry = null;
    }

    return {
      ...values,
      clientId: dataStepGeneral.clientId,
      contractId: isMoralP
        ? dataStepPersonal.contractId
        : dataStepGeneral.contractId,
      depositAmountCurrency:
        values.depositAmountCurrency && values.depositAmountCurrency.value,
      depositAmountType:
        values.depositAmountType && values.depositAmountType.value,
      depositFrequencyType:
        values.depositFrequencyType && values.depositFrequencyType.value,
      resourceDestination:
        values.resourceDestination &&
        values.resourceDestination.map((i: any) => i.value),
      resourceOrigin:
        values.resourceOrigin && values.resourceOrigin.map((i: any) => i.value),
      withdrawalAmountCurrency:
        values.withdrawalAmountCurrency &&
        values.withdrawalAmountCurrency.value,
      withdrawalAmountType:
        values.withdrawalAmountType && values.withdrawalAmountType.value,
      withdrawalFrequencyType:
        values.withdrawalFrequencyType && values.withdrawalFrequencyType.value,
      withdrawalAmount: parseFloat(values.withdrawalAmount.value),
      depositAmount: parseFloat(values.depositAmount.value),
      receivesForeignTransfers: values.receivesForeignTransfers
        ? `${values.receivesForeignTransfers}`
        : null,
      sendsForeignTransfers: values.sendsForeignTransfers
        ? `${values.sendsForeignTransfers}`
        : null,
      cveForeignTransferCountry: cveForeignTransferCountry,
    };
  };

  const savePldStep = async (values: any) => {
    if (isMoralP) {
      return dataStepPLD.saved
        ? await updatePldMoral(values)
        : await createPldMoral(values);
    }
    return await savePld(values);
  };

  const saveStepPLD = async (values: any) => {
    const finalValues = isMoralP
      ? pldMoralPersonDataRemapper(values)
      : PLDDataRemapper(values);
    const saveStepResponse = await savePldStep(finalValues);

    if (saveStepResponse.success) {
      setDataStepPLD({ ...finalValues, ...saveStepResponse, saved: true });
      return { success: true };
    }
    return { success: false };
  };

  const resetPLD = () => setDataStepPLD(initialState);

  return (
    <PLDSaveSteps.Provider
      value={{
        dataStepPLD,
        saveStepPLD,
        setDataStepPLD,
        resetPLD,
      }}
    >
      {children}
    </PLDSaveSteps.Provider>
  );
};

export default PLDSaveStepsProvider;
